<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">对话书籍管理</h1>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理 Dialogue 书籍的基本信息与章节入口</p>
    </div>

    <div class="mb-4 space-y-4">
      <div class="flex gap-4">
        <div class="flex-1">
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="搜索对话书籍标题或描述..."
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            @input="handleSearch"
          />
        </div>
        <div class="flex items-center gap-2">
          <button @click="loadBooks" class="cancel-btn">
            刷新
          </button>
          <button @click="handleCreate" class="primary-btn gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            新增书籍
          </button>
        </div>
      </div>

      <div class="flex gap-4">
        <select
          v-model="filters.categoryId"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @change="handleFilterChange"
        >
          <option :value="undefined">全部分类</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">
            {{ category.name }}
          </option>
        </select>
        <select
          v-model="filters.difficultyLevel"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @change="handleFilterChange"
        >
          <option :value="undefined">全部难度</option>
          <option :value="1">★☆☆☆☆ (入门)</option>
          <option :value="2">★★☆☆☆ (初级)</option>
          <option :value="3">★★★☆☆ (中级)</option>
          <option :value="4">★★★★☆ (高级)</option>
          <option :value="5">★★★★★ (专家)</option>
        </select>
        <select
          v-model="filters.isPublic"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @change="handleFilterChange"
        >
          <option :value="undefined">全部状态</option>
          <option :value="true">公开</option>
          <option :value="false">私有</option>
        </select>
        <select
          v-model="filters.isFree"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @change="handleFilterChange"
        >
          <option :value="undefined">全部类型</option>
          <option :value="true">免费</option>
          <option :value="false">付费</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 overflow-hidden">
      <BookTable :books="books" @view="handleView" @edit="handleEdit" @delete="handleDelete" />
      <Pagination
        v-if="total > 0"
        :total="total"
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        @update:current-page="loadBooks"
        @update:page-size="loadBooks"
      />
    </div>

    <Modal :show="showModal" @close="handleCancel">
      <template #header>
        {{ editingBook ? '编辑对话书籍' : '新增对话书籍' }}
      </template>
      <template #body>
        <BookForm
          :initial-data="formData"
          :categories="categories"
          :loading="submitting"
          @submit="handleSubmit"
          @cancel="handleCancel"
        />
      </template>
    </Modal>

    <ConfirmDialog
      :show="showDeleteConfirm"
      type="danger"
      title="确认删除"
      :message="`确定要删除对话书籍「${deletingBook?.title}」吗？此操作不可恢复。`"
      confirm-text="删除"
      cancel-text="取消"
      @confirm="confirmDelete"
      @cancel="() => (showDeleteConfirm = false)"
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import BookForm from '@/components/dictation/BookForm.vue'
import BookTable from '@/components/dictation/BookTable.vue'
import Modal from '@/components/ui/Modal.vue'
import Pagination from '@/components/ui/Pagination.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import { getCategoryListAPI } from '@/api/dictation/category'
import {
  createDialogueBookAPI,
  deleteDialogueBookAPI,
  getDialogueBookListAPI,
  updateDialogueBookAPI,
} from '@/api/dictation/dialogue'
import type { CategoryWithSubCategories } from '@/types/dictation/category'
import type { DialogueBook } from '@/types/dictation/dialogue'

const router = useRouter()

const loading = ref(false)
const submitting = ref(false)
const books = ref<DialogueBook[]>([])
const categories = ref<CategoryWithSubCategories[]>([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(20)
const searchKeyword = ref('')

const filters = ref({
  categoryId: undefined as number | undefined,
  difficultyLevel: undefined as number | undefined,
  isPublic: undefined as boolean | undefined,
  isFree: undefined as boolean | undefined,
})

const showModal = ref(false)
const showDeleteConfirm = ref(false)
const editingBook = ref<DialogueBook | null>(null)
const deletingBook = ref<DialogueBook | null>(null)

const formData = ref({
  title: '',
  description: '',
  category_id: null as number | null,
  subcategory_id: null as number | null,
  language_id: 1,
  difficulty_level: 3,
  is_public: true,
  is_free: true,
})

const loadBooks = async () => {
  loading.value = true
  try {
    const response = await getDialogueBookListAPI({
      page: currentPage.value,
      limit: pageSize.value,
      search: searchKeyword.value || undefined,
      categoryId: filters.value.categoryId,
      difficultyLevel: filters.value.difficultyLevel,
      isPublic: filters.value.isPublic,
      isFree: filters.value.isFree,
    })
    books.value = response.data?.books || []
    total.value = response.data?.total || 0
  } finally {
    loading.value = false
  }
}

const loadCategories = async () => {
  const response = await getCategoryListAPI(true, true)
  categories.value = response.data?.categories || []
}

const handleSearch = () => {
  currentPage.value = 1
  loadBooks()
}

const handleFilterChange = () => {
  currentPage.value = 1
  loadBooks()
}

const handleCreate = () => {
  editingBook.value = null
  formData.value = {
    title: '',
    description: '',
    category_id: null,
    subcategory_id: null,
    language_id: 1,
    difficulty_level: 3,
    is_public: true,
    is_free: true,
  }
  showModal.value = true
}

const handleEdit = (book: DialogueBook) => {
  editingBook.value = book
  formData.value = {
    title: book.title,
    description: book.description || '',
    category_id: book.categoryId ?? null,
    subcategory_id: book.subcategoryId ?? null,
    language_id: book.languageId,
    difficulty_level: book.difficultyLevel,
    is_public: book.isPublic,
    is_free: book.isFree,
  }
  showModal.value = true
}

const handleView = (book: DialogueBook) => {
  router.push(`/dictation/dialogue/books/${book.id}`)
}

const handleDelete = (book: DialogueBook) => {
  deletingBook.value = book
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  if (!deletingBook.value) return
  await deleteDialogueBookAPI(deletingBook.value.id)
  showDeleteConfirm.value = false
  deletingBook.value = null
  await loadBooks()
}

const handleSubmit = async (data: any) => {
  submitting.value = true
  try {
    if (editingBook.value) {
      await updateDialogueBookAPI(editingBook.value.id, data)
    } else {
      await createDialogueBookAPI(data)
    }
    showModal.value = false
    await loadBooks()
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  showModal.value = false
}

onMounted(async () => {
  await Promise.all([loadCategories(), loadBooks()])
})
</script>
